// src/pages/SocioPanel.jsx
import { useEffect, useMemo, useState, useCallback, lazy, Suspense, useRef } from "react";
import { useParams, Link, useNavigate, useLocation } from "react-router-dom";
import { authFetch, getToken, isEmpleado, getSocioId } from "../services/auth";
import ModalDocumentos from "../components/ModalDocumentos";
import { openDocSecure } from "../utils/openDocSecure";
const ChecklistSocio = lazy(() => import("../components/ChecklistSocio"));

/* -------------------- Utilidades base -------------------- */
const DASH = "—";
const safe = (v, empty = DASH) =>
  v === null || v === undefined || v === "" ? empty : v;

const fmtDate = (s, empty = DASH) => {
  if (!s) return empty;
  try {
    const d = new Date(s + "T00:00:00");
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${dd}/${mm}/${yyyy}`;
  } catch {
    return empty;
  }
};

/* -------------------- Avatar local (sin red) -------------------- */
function initialsFrom(name) {
  const parts = String(name || "Usuario").trim().split(/\s+/);
  const a = parts[0]?.[0] || "U";
  const b = parts[1]?.[0] || "";
  return (a + b).toUpperCase();
}
function hashHue(str) {
  let h = 0;
  const s = String(str || "");
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return h % 360;
}
function makeAvatarDataUrl(name) {
  const txt = initialsFrom(name);
  const hue = hashHue(name || "Usuario");
  const bg = `hsl(${hue}, 70%, 45%)`;
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="128" height="128">
      <rect width="100%" height="100%" fill="${bg}"/>
      <text x="50%" y="50%" dy=".35em" text-anchor="middle"
            font-family="Inter, system-ui, Segoe UI, Arial" font-size="54"
            fill="#fff" font-weight="600">${txt}</text>
    </svg>`;
  return "data:image/svg+xml;utf8," + encodeURIComponent(svg);
}

/* -------------------- MOCKS UI (fallbacks) -------------------- */
const mockSocio = (id) => ({
  id: Number(id) || 1,
  nombre: "Nombre",
  apellidos: "Apellidos",
  dni: "00000000X",
  matricula: "PENDIENTE-01",
  email: "correo@ejemplo.com",
  telefono: "600000000",
  direccion: "Calle ejemplo 123",
  cap: "CAP-123",
  permiso_conduccion: "B+E",
  tarjeta_sanitaria: "TS-123",
  cuenta_bancaria: "ES00 0000 0000 0000 0000 0000",
  avatarUrl: "",
  fecha_baja: null,
});

/* Deriva metadatos de aura desde el payload del checklist */
function deriveChecklistMeta(resp) {
  const hoy = new Date(); hoy.setHours(0, 0, 0, 0);
  const blocks = Array.isArray(resp?.blocks) ? resp.blocks : [];
  const items = blocks.flatMap((b) => (Array.isArray(b.items) ? b.items : []));
  const incompleto = items.some((it) => it?.estado !== "subido");
  let hasExpired = false;
  let hasSoon = false;
  for (const it of items) {
    const cad = it?.caducidad;
    if (!cad) continue;
    const d = new Date(`${cad}T00:00:00`);
    const diff = Math.ceil((d - hoy) / 86400000);
    if (diff < 0) { hasExpired = true; break; }
    if (diff <= 30) hasSoon = true;
  }
  const summary = resp?.summary || { pendientes_total: 0, total: 0 };
  return { incompleto, hasExpired, hasSoon, summary };
}

/* ---- Estado del socio (Activo / En trámite / Baja) ---- */
function computeSocioStatus({ fecha_baja, enTramiteHeader, fmtDateFn = (s) => s }) {
  if (fecha_baja) {
    return {
      key: "baja",
      label: `Baja · ${fmtDateFn(fecha_baja)}`,
      chipClass: "bg-red-100 text-red-800",
      dotClass: "bg-red-600",
    };
  }
  if (enTramiteHeader) {
    return {
      key: "tramite",
      label: "En trámite",
      chipClass: "bg-indigo-100 text-indigo-800",
      dotClass: "bg-indigo-600",
    };
  }
  return {
    key: "activo",
    label: "Activo",
    chipClass: "bg-green-100 text-green-800",
    dotClass: "bg-green-600",
  };
}
function StatusChip({ status }) {
  if (!status) return null;
  return (
    <div className={`mt-1 inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full ${status.chipClass}`}>
      <span className={`inline-block w-1.5 h-1.5 rounded-full ${status.dotClass}`} />
      {status.label}
    </div>
  );
}

/* ---------- Helpers Conductores ---------- */
const N = (s) => (s || "").toString().trim().toUpperCase().replace(/\s+/g, "_");

function baseConductorPrefix(socio) {
  return `/uploads/${N(socio?.nombre || "NOMBRE")}_${N(socio?.matricula || "0000XXX")}/CONDUCTORES/`;
}

function deriveConductoresStats(docs = [], socio) {
  const base = baseConductorPrefix(socio);
  const map = new Map(); // name -> { dpCount, nominasCount }
  for (const d of Array.isArray(docs) ? docs : []) {
    const rawPath = d.archivo ?? d.path ?? "";
    const path = typeof rawPath === "string" ? rawPath : "";
    if (!path?.startsWith(base)) continue;
    const resto = path.slice(base.length);              // <Nombre>/...
    const parts = resto.split("/").filter(Boolean);
    if (parts.length < 2) continue;
    const nombre = parts[0];
    const sub1 = (parts[1] || "").toUpperCase();
    const entry = map.get(nombre) || { dpCount: 0, coopCount: 0, nominasCount: 0 };
    if (sub1 === "DATOS_PERSONALES") entry.dpCount += 1;
    if (sub1 === "DATOS_COOPERATIVA") entry.coopCount += 1;
    if (sub1 === "NOMINAS") entry.nominasCount += 1;
    map.set(nombre, entry);
  }
  return Array.from(map.entries()).map(([nombre, stats]) => ({ nombre, ...stats }));
}

function listFilesForConductor(docs = [], socio, nombre) {
  const base = baseConductorPrefix(socio) + `${nombre}/`;
  const out = { dp: [], coop: [], nominas: [] };
  for (const d of Array.isArray(docs) ? docs : []) {
    const path = d.archivo || d.path || "";
    if (!path?.startsWith(base)) continue;
    const resto = path.slice(base.length);              // DATOS_PERSONALES/xxx | NOMINAS/xxx
    const top = (resto.split("/")[0] || "").toUpperCase();
    const item = {
      id: d.id,
      nombre: d.nombre_archivo || d.nombre || "documento",
      fecha_caducidad: d.fecha_caducidad || null,
    };
    if (top === "DATOS_PERSONALES") out.dp.push(item);
    if (top === "DATOS_COOPERATIVA") out.coop.push(item);
    if (top === "NOMINAS") out.nominas.push(item);
  }
  // orden: caducidad más próxima primero
  const srt = (a, b) => {
    const da = a.fecha_caducidad ? new Date(a.fecha_caducidad).getTime() : Infinity;
    const db = b.fecha_caducidad ? new Date(b.fecha_caducidad).getTime() : Infinity;
    return da - db;
  };
  out.dp.sort(srt); out.coop.sort(srt); out.nominas.sort(srt);
  return out;
}

function avatarFromName(name) {
  const safeName = (typeof name === "string" && name.trim()) ? name.trim() : "Conductor";
  const parts = safeName.split(/\s+/);
  const a = parts[0]?.[0] || "C";
  const b = parts[1]?.[0] || "";
  const txt = (a + b).toUpperCase();
  let h = 0; for (let i = 0; i < safeName.length; i++) h = (h * 31 + safeName.charCodeAt(i)) >>> 0;
  const hue = h % 360;
  const bg = `hsl(${hue}, 70%, 45%)`;
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="96" height="96">
      <rect width="100%" height="100%" fill="${bg}"/>
      <text x="50%" y="50%" dy=".35em" text-anchor="middle"
        font-family="Inter, system-ui, Segoe UI, Arial" font-size="38"
        fill="#fff" font-weight="600">${txt}</text>
    </svg>`;
  return "data:image/svg+xml;utf8," + encodeURIComponent(svg);
}

function BadgeEstado({ fecha }) {
  if (!fecha) return <span className="text-xs px-2 py-0.5 rounded bg-gray-100 text-gray-700">Sin caducidad</span>;
  const hoy = new Date(); hoy.setHours(0, 0, 0, 0);
  const f = new Date(`${fecha}T00:00:00`);
  const diff = Math.ceil((f - hoy) / 86400000);
  if (diff < 0) return <span className="text-xs px-2 py-0.5 rounded bg-red-100 text-red-800">Caducado</span>;
  if (diff <= 30) return <span className="text-xs px-2 py-0.5 rounded bg-amber-100 text-amber-800">≤ 30 días</span>;
  return <span className="text-xs px-2 py-0.5 rounded bg-green-100 text-green-800">Vigente</span>;
}

/* -------------------- Helpers API extra (ficha conductor + ZIP) -------------------- */
async function fetchConductorDatos(socioId, nombre) {
  const res = await authFetch(
    `/api/documentos/${socioId}/conductores/datos?nombre=${encodeURIComponent(nombre)}`
  );
  if (!res.ok) throw new Error("No se pudo cargar la ficha del conductor");
  const data = await res.json();
  return {
    telefono: data.telefono ?? data.phone ?? "",
    dni: data.dni ?? data.NIF ?? "",
    email: data.email ?? "",
    naf: data.naf ?? data.num_afiliacion ?? "",
    cuenta_bancaria: data.cuenta_bancaria ?? data.iban ?? "",
    direccion: data.direccion ?? data.address ?? "",
    neto: data.neto ?? data.importe_neto ?? "",
  };
}
async function saveConductorDatos(socioId, nombre, payload) {
  const body = { nombre, ...payload };
  const res = await authFetch(`/api/documentos/${socioId}/conductores/datos`, {
    method: "PUT",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const txt = await res.text().catch(() => "");
    throw new Error(txt || "No se pudo guardar la ficha del conductor");
  }
  return res.json().catch(() => ({}));
}

async function downloadZipForPaths(socioId, paths = [], suggestedName = "documentos.zip") {
  if (!Array.isArray(paths) || !paths.length) return;
  try {
    const qs = paths.map((p) => `paths=${encodeURIComponent(p)}`).join("&");
    const res = await authFetch(`/api/documentos/${socioId}/zip?${qs}`);
    if (!res.ok) throw new Error("No se pudo generar el ZIP");
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = suggestedName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => { try { URL.revokeObjectURL(url); } catch {} }, 3000);
  } catch (e) {
    alert(e?.message || "Error descargando ZIP");
  }
}

/* -------------------- Componente principal -------------------- */
export default function SocioPanel({ role = "empleado" }) {
  const navigate = useNavigate();
  const { id } = useParams();
  const socioId = Number(id) || 1;

  // Query params
  const loc = useLocation();
  const usp = useMemo(() => new URLSearchParams(loc?.search || ""), [loc?.search]);
  const safeParam = (usp.get("safe") || "").toLowerCase();
  const SAFE_ALL = ["1", "true", "on"].includes(safeParam);
  const SAFE_CHECKLIST = SAFE_ALL || safeParam === "checklist";
  const SAFE_DOCS = SAFE_ALL || safeParam === "docs";
  const SAFE_ANY = SAFE_CHECKLIST || SAFE_DOCS;

  const [socio, setSocio] = useState(() => mockSocio(socioId));
  const [resumen, setResumen] = useState(null);
  const [docs, setDocs] = useState([]);

  const [tab, setTab] = useState("documentos");
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(() => ({
    email: socio.email || "",
    telefono: socio.telefono || "",
    direccion: socio.direccion || "",
    matricula: socio.matricula || "",
  }));
  const [saving, setSaving] = useState(false);

  // Abrir gestor en categoría/subcarpeta concreta
  const [initialCategory, setInitialCategory] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [docsSub, setDocsSub] = useState("");

  // Checklist “vivo”
  const [checkMeta, setCheckMeta] = useState({
    incompleto: false,
    hasExpired: false,
    hasSoon: false,
    summary: { pendientes_total: 0, total: 0 },
  });
  const [checklistRefreshKey, setChecklistRefreshKey] = useState(0);

  // --- Baja / Reactivación ---
  const [bajaModalOpen, setBajaModalOpen] = useState(false);
  const [bajaDate, setBajaDate] = useState(() =>
    new Date().toISOString().slice(0, 10)
  );
  const [savingBaja, setSavingBaja] = useState(false);

  const puedeAdministrar = isEmpleado();
  const alertLevel = useMemo(() => getAlertLevel(resumen), [resumen]);

  const fullName = useMemo(() => {
    const parts = [socio?.nombre, socio?.apellidos].filter(Boolean);
    return parts.length ? parts.join(" ") : "Sin nombre";
  }, [socio]);

  const avatar = useMemo(() => {
    if (socio?.avatarUrl) return socio.avatarUrl;
    return makeAvatarDataUrl(fullName === "Sin nombre" ? "Usuario" : fullName);
  }, [socio?.avatarUrl, fullName]);

  // -------------------- Conductores UI --------------------
  const [conductoresUI, setConductoresUI] = useState({ list: [], loading: false, err: "" });
  const [newCond, setNewCond] = useState({ nombre: "", apellidos: "", dni: "" });
  const [creatingCond, setCreatingCond] = useState(false);

  /* ---------- Guards: token y pertenencia ---------- */
  useEffect(() => {
    if (!getToken()) return;
    if (!isEmpleado()) {
      const myId = getSocioId();
      if (myId != null && myId !== socioId) {
        navigate(`/socios/${myId}`, { replace: true });
        return;
      }
    }
  }, [socioId, navigate]);

  /* ---------- Carga desde API ---------- */
  useEffect(() => {
    if (!getToken()) return;
    let alive = true;

    const ctrlSoc = new AbortController();
    const ctrlRes = new AbortController();
    const ctrlDocs = new AbortController();
    const ctrlChk = new AbortController();

    (async () => {
      try {
        const r = await authFetch(`/api/socios/${socioId}`, { signal: ctrlSoc.signal });
        if (alive && r.ok) setSocio(await r.json());
      } catch {}

      try {
        const r2 = await authFetch(`/api/socios/${socioId}/resumen`, { signal: ctrlRes.signal });
        if (alive && r2.ok) setResumen(await r2.json());
      } catch {}

      if (!SAFE_DOCS) {
        try {
          const r3 = await authFetch(`/api/documentos/socio/${socioId}`, { signal: ctrlDocs.signal });
          if (alive && r3.ok) setDocs(await r3.json());
        } catch {}
      } else {
        setDocs([]);
      }

      if (!SAFE_CHECKLIST) {
        try {
          const r4 = await authFetch(`/api/socios/${socioId}/checklist`, { signal: ctrlChk.signal });
          if (alive && r4.ok) setCheckMeta(deriveChecklistMeta(await r4.json()));
        } catch {}
      } else {
        setCheckMeta({ incompleto: false, hasExpired: false, hasSoon: false, summary: { pendientes_total: 0, total: 0 } });
      }
    })();

    return () => {
      alive = false;
      ctrlSoc.abort(); ctrlRes.abort(); ctrlDocs.abort(); ctrlChk.abort();
    };
  }, [socioId, SAFE_DOCS, SAFE_CHECKLIST]);

  /* ---------- Sincroniza formulario con socio ---------- */
  useEffect(() => {
    setForm({
      email: socio.email || "",
      telefono: socio.telefono || "",
      direccion: socio.direccion || "",
      matricula: socio.matricula || "",
    });
  }, [socio]);

  // Recarga tras cambios en el Modal (subidas/renombres/borrados)
  const reloadDocsAndResumen = useCallback(async () => {
    try {
      const [r2, r3, r4] = await Promise.all([
        authFetch(`/api/socios/${socioId}/resumen`),
        SAFE_DOCS ? Promise.resolve({ ok: false }) : authFetch(`/api/documentos/socio/${socioId}`),
        SAFE_CHECKLIST ? Promise.resolve({ ok: false }) : authFetch(`/api/socios/${socioId}/checklist`),
      ]);

      if (r2.ok) setResumen(await r2.json());
      if (r3.ok) setDocs(await r3.json());
      if (r4.ok) setCheckMeta(deriveChecklistMeta(await r4.json()));
    } catch (e) {
      console.error("❌ reloadDocsAndResumen:", e);
    }
  }, [socioId, SAFE_DOCS, SAFE_CHECKLIST]);

  // Abrir gestor en categoría/subcarpeta
  const openDocs = (categoria = null, subcarpeta = "") => {
    if (!puedeAdministrar || SAFE_DOCS) {
      setTab("documentos");
      return;
    }
    setInitialCategory(categoria);
    setDocsSub(subcarpeta || "");
    setModalOpen(true);
  };

  // === Conductores API: listar/crear/eliminar ===
  const loadConductores = useCallback(async () => {
    if (!socioId) return;
    setConductoresUI((u) => ({ ...u, loading: true, err: "" }));
    try {
      const res = await authFetch(
        `/api/documentos/${socioId}/carpetas?categoria=${encodeURIComponent("CONDUCTORES")}`
      );
      if (!res.ok) throw new Error("No se pudieron cargar los conductores");
      const data = await res.json();
      const top = (Array.isArray(data) ? data : [])
        .filter((n) => typeof n === "string" && n.trim().length > 0)
        .filter((n) => !n.includes("/"))
        .map((n) => n.trim())
        .sort((a, b) => a.localeCompare(b, "es"));
      setConductoresUI({ list: top, loading: false, err: "" });
    } catch (e) {
      setConductoresUI({ list: [], loading: false, err: e?.message || "Error" });
    }
  }, [socioId]);

  const crearConductor = useCallback(async () => {
    const nombre = (newCond.nombre || "").trim();
    const apellidos = (newCond.apellidos || "").trim();
    const pretty = [nombre, apellidos].filter(Boolean).join(" ").trim();

    if (!pretty) {
      alert("Introduce al menos nombre y/o apellidos del conductor.");
      return;
    }

    setCreatingCond(true);
    try {
      // raíz
      let res = await authFetch(`/api/documentos/${socioId}/carpeta`, {
        method: "POST",
        headers: { Accept: "application/json", "Content-Type": "application/json" },
        body: JSON.stringify({ categoria: "CONDUCTORES", nombre_subcarpeta: pretty }),
      });
      if (!res.ok) throw new Error("No se pudo crear carpeta del conductor");

      // subcarpetas (anidadas)
      for (const sub of ["DATOS_PERSONALES", "DATOS_COOPERATIVA", "NOMINAS"]) {
        res = await authFetch(`/api/documentos/${socioId}/carpeta`, {
          method: "POST",
          headers: { Accept: "application/json", "Content-Type": "application/json" },
          body: JSON.stringify({ categoria: "CONDUCTORES", nombre_subcarpeta: `${pretty}/${sub}` }),
        });
        if (!res.ok) throw new Error(`No se pudo crear ${sub} del conductor`);
      }

      setNewCond({ nombre: "", apellidos: "", dni: "" });
      await loadConductores();

      // Abrir gestor directo (empleado) — opcional
      if (puedeAdministrar) openDocs("CONDUCTORES", `${pretty}/DATOS_PERSONALES`);
    } catch (e) {
      alert(e?.message || "Error creando conductor");
    } finally {
      setCreatingCond(false);
    }
  }, [newCond, socioId, loadConductores, puedeAdministrar]);

  // Eliminar conductor (borra subcarpetas + raíz). Requiere soporte backend DELETE.
  const eliminarConductor = useCallback(async (nombre) => {
    if (!window.confirm(`¿Eliminar al conductor "${nombre}" y TODA su documentación? Esta acción no se puede deshacer.`)) return;
    try {
      // Intento #1: DELETE con body JSON (backend FastAPI típico)
      const del = async (sub) => {
        const body = { categoria: "CONDUCTORES", nombre_subcarpeta: sub, recursive: true };
        const r = await authFetch(`/api/documentos/${socioId}/carpeta`, {
          method: "DELETE",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify(body),
        });
        return r.ok;
      };

      // Borramos ramas profundas primero
      await del(`${nombre}/NOMINAS`).catch(() => {});
      await del(`${nombre}/DATOS_PERSONALES`).catch(() => {});
      const okRoot = await del(`${nombre}`);

      if (!okRoot) {
        // Intento #2: DELETE por querystring si el backend lo espera así
        const qs = (sub) =>
          `/api/documentos/${socioId}/carpeta?categoria=${encodeURIComponent("CONDUCTORES")}&nombre_subcarpeta=${encodeURIComponent(sub)}&recursive=1`;
        await authFetch(qs(`${nombre}/NOMINAS`), { method: "DELETE" }).catch(() => {});
        await authFetch(qs(`${nombre}/DATOS_PERSONALES`), { method: "DELETE" }).catch(() => {});
        await authFetch(qs(`${nombre}`), { method: "DELETE" });
      }

      await loadConductores();
      await reloadDocsAndResumen();
      alert("Conductor eliminado.");
    } catch (e) {
      alert(e?.message || "No se pudo eliminar el conductor");
    }
  }, [socioId, loadConductores, reloadDocsAndResumen]);

  // Dar de baja conductor (mover a CONDUCTORES_BAJA/<Nombre> con fallback a move)
  const darDeBajaConductor = useCallback(async (nombre) => {
    if (!window.confirm(`¿Marcar "${nombre}" como de baja? Se moverá su carpeta a CONDUCTORES_BAJA/${nombre}.`)) return;
    try {
      // Primer intento: endpoint estándar
      const r = await authFetch(`/api/documentos/${socioId}/conductores/baja`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ nombre }),
      });
      if (!r.ok) {
        // Fallback: mover manualmente
        const r2 = await authFetch(`/api/documentos/${socioId}/move`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify({
            from: `CONDUCTORES/${nombre}`,
            to: `CONDUCTORES_BAJA/${nombre}`,
          }),
        });
        if (!r2.ok) {
          const txt = await r2.text().catch(() => "");
          throw new Error(txt || "No se pudo dar de baja (mover)");
        }
      }
      await loadConductores();
      await reloadDocsAndResumen();
      alert(`"${nombre}" se marcó como de baja correctamente.`);
    } catch (e) {
      alert(e?.message || "Error al dar de baja");
    }
  }, [socioId, loadConductores, reloadDocsAndResumen]);

  // Autocargar conductores al entrar en su pestaña
  useEffect(() => {
    if (tab === "conductores") loadConductores();
  }, [tab, loadConductores]);

  const onChangeAvatar = async (file) => {
    if (!file) return;
    const fake = URL.createObjectURL(file);
    setSocio((s) => ({ ...s, avatarUrl: fake }));
  };

  const saveDatos = async () => {
    if (form.email && !/^\S+@\S+\.\S+$/.test(form.email)) {
      alert("Correo no válido");
      return;
    }
    setSaving(true);
    try {
      const res = await authFetch(`/api/socios/${socioId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error("Error al guardar");
      const updated = await res.json();
      setSocio(updated);
      setEditing(false);
    } catch {
      alert("No se pudieron guardar los cambios");
    } finally {
      setSaving(false);
    }
  };

  // ---- Baja / reactivación socio ----
  async function refetchSocio() {
    try {
      const r = await authFetch(`/api/socios/${socioId}`);
      if (r.ok) setSocio(await r.json());
    } catch {}
  }
  async function marcarBaja(fechaStr) {
    setSavingBaja(true);
    try {
      const body = {
        fecha_baja: fechaStr,
        fechaBaja: fechaStr,
        en_baja: true,
        enBaja: true,
        estado: "BAJA",
        activo: false,
      };
      const r = await authFetch(`/api/socios/${socioId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!r.ok) throw new Error("No se pudo marcar la baja");
      setBajaModalOpen(false);
      await refetchSocio();
    } catch (e) {
      alert(e?.message || "Error marcando baja");
    } finally {
      setSavingBaja(false);
    }
  }
  async function reactivarSocio() {
    if (!window.confirm("¿Reactivar a este socio? Se eliminará la fecha de baja.")) return;
    setSavingBaja(true);
    try {
      const body = {
        fecha_baja: null,
        fechaBaja: null,
        en_baja: false,
        enBaja: false,
        estado: "ACTIVO",
        activo: true,
      };
      const r = await authFetch(`/api/socios/${socioId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!r.ok) throw new Error("No se pudo reactivar");
      await refetchSocio();
    } catch (e) {
      alert(e?.message || "Error reactivando");
    } finally {
      setSavingBaja(false);
    }
  }

  // --- Aura / estados derivados ---
  const incompleto = checkMeta.incompleto;
  const pendientesTotal = useMemo(
    () => Number(checkMeta?.summary?.pendientes_total || 0),
    [checkMeta]
  );
  const hasExpiredInChecklist = checkMeta.hasExpired;
  const hasSoonInChecklist = checkMeta.hasSoon;

  const socioStatus = computeSocioStatus({
    fecha_baja: socio?.fecha_baja,
    enTramiteHeader: (pendientesTotal > 0) || incompleto,
    fmtDateFn: fmtDate,
  });

  const hasRed = alertLevel === "high" || hasExpiredInChecklist;
  const hasAmber = !hasRed && (alertLevel === "med" || hasSoonInChecklist);

  const auraClass = hasRed
    ? "ring-2 ring-red-400/60 ring-offset-2 motion-safe:animate-pulse"
    : hasAmber
    ? "ring-2 ring-amber-300/50 ring-offset-2"
    : incompleto
    ? "ring-2 ring-indigo-400/60 ring-offset-2"
    : "";

  /* -------------------- RENDER -------------------- */
  return (
    <div className="p-6 space-y-6">
      {/* Migas / volver */}
      <div className="flex items-center justify-between">
        <div className="text-sm text-gray-500">
          {puedeAdministrar ? (
            <Link to="/socios" className="hover:underline">← Volver a la lista</Link>
          ) : null}
        </div>
        <div className="text-xs text-gray-400">ID: {socio.id}</div>
      </div>

      {/* Header */}
      <header className={`bg-white rounded-2xl shadow p-5 transition-all ${auraClass}`}>
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          {/* Avatar + nombre */}
          <div className="flex items-center gap-4">
            <div className="relative">
              <img
                src={avatar}
                alt={fullName}
                className="w-20 h-20 rounded-full object-cover border"
                width={80}
                height={80}
                loading="lazy"
                decoding="async"
                draggable={false}
              />
              {puedeAdministrar && (
                <label className="absolute -bottom-1 -right-1 bg-gray-900 text-white text-xs px-2 py-1 rounded-full cursor-pointer hover:bg-black">
                  Cambiar
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => onChangeAvatar(e.target.files?.[0])}
                  />
                </label>
              )}
            </div>

            <div>
              <h1 className="text-2xl font-semibold">{fullName}</h1>

              {SAFE_ANY && (
                <div className="mt-1 inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">
                  <span className="inline-block w-1.5 h-1.5 rounded-full bg-amber-600" />
                  Modo seguro activo
                </div>
              )}

              <StatusChip status={socioStatus} />

              <p className="text-gray-600">
                Matrícula: <span className="font-mono">{safe(socio?.matricula)}</span> · DNI: {safe(socio?.dni)}
              </p>
              <p className="text-gray-600">
                {safe(socio?.email, "Sin email")} · {safe(socio?.telefono, "Sin teléfono")}
              </p>
            </div>
          </div>

          {/* Acciones rápidas (solo empleado) */}
          {puedeAdministrar && (
            <div className="flex flex-wrap items-center gap-2">
              <button
                disabled={SAFE_DOCS}
                onClick={() => openDocs()}
                className={`px-3 py-2 rounded-2xl ${SAFE_DOCS ? "bg-gray-300 text-gray-600" : "bg-blue-600 text-white hover:bg-blue-700"} shadow`}
              >
                Ver documentos
              </button>
              <button
                onClick={() => setTab("conductores")}
                className="px-3 py-2 rounded-2xl bg-white border shadow hover:bg-gray-50"
                title="Ir a Conductores y dar de alta uno nuevo"
              >
                Alta de conductor
              </button>
              <button
                disabled={SAFE_DOCS}
                onClick={() => openDocs("DATOS PERSONALES")}
                className="px-3 py-2 rounded-2xl bg-white border shadow hover:bg-gray-50 disabled:opacity-50"
              >
                Crear subcarpeta
              </button>
              <button
                disabled={SAFE_DOCS}
                onClick={() => openDocs("DOCUMENTOS VEHÍCULO")}
                className="px-3 py-2 rounded-2xl bg-white border shadow hover:bg-gray-50 disabled:opacity-50"
              >
                Subir documento
              </button>

              {/* Baja / Reactivar */}
              {socioStatus?.key !== "baja" ? (
                <button
                  onClick={() => { setBajaDate(new Date().toISOString().slice(0,10)); setBajaModalOpen(true); }}
                  className="px-3 py-2 rounded-2xl bg-red-600 text-white shadow hover:bg-red-700"
                >
                  Dar de baja
                </button>
              ) : (
                <button
                  onClick={reactivarSocio}
                  disabled={savingBaja}
                  className="px-3 py-2 rounded-2xl bg-green-600 text-white shadow hover:bg-green-700 disabled:opacity-60"
                >
                  {savingBaja ? "Reactivando…" : "Reactivar"}
                </button>
              )}
            </div>
          )}
        </div>

        {/* Atajos por categoría */}
        {!SAFE_DOCS && (
          <div className="mt-4 flex flex-wrap gap-2">
            <CategoriaChip label="Datos personales"    category="DATOS PERSONALES"    onOpen={openDocs} disabled={!puedeAdministrar} />
            <CategoriaChip label="Documentos vehículo" category="DOCUMENTOS VEHÍCULO" onOpen={openDocs} disabled={!puedeAdministrar} />
            <CategoriaChip label="CMR"                 category="CMR"                 onOpen={openDocs} disabled={!puedeAdministrar} />
            <CategoriaChip label="Liquidaciones"       category="LIQUIDACIONES"       onOpen={openDocs} disabled={!puedeAdministrar} />
          </div>
        )}
      </header>

      {/* Widgets resumen */}
      <section className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Checklist “vivo” */}
        <div className="bg-white rounded-2xl shadow p-4">
          {SAFE_CHECKLIST ? (
            <div className="text-sm text-gray-500">Checklist desactivado en modo seguro.</div>
          ) : (
            <Suspense fallback={<div className="text-sm text-gray-500">Cargando checklist…</div>}>
              <ChecklistSocio
                socioId={socioId}
                onOpenDocs={openDocs}
                refreshKey={checklistRefreshKey}
                docs={docs}
                maxHeight={300}
                dense
                onlyPending
                collapsedLimit={6}
                defaultCollapsed
                onSummaryChange={(summary) => {
                  const pend = typeof summary === "number"
                    ? summary
                    : Number(summary?.pendientes_total ?? summary?.summary?.pendientes_total ?? 0);
                  setCheckMeta((m) => ({
                    ...m,
                    summary: { pendientes_total: isFinite(pend) ? pend : 0, total: m.summary?.total ?? 0 },
                  }));
                }}
              />
            </Suspense>
          )}
        </div>

        <div className="bg-white rounded-2xl shadow p-4">
          <div className="text-sm text-gray-500">Por caducar (≤60 días)</div>
          <div className="text-3xl font-bold">{resumen?.documentos_por_caducar ?? "-"}</div>
        </div>

        <div className="bg-white rounded-2xl shadow p-4">
          <div className="text-sm text-gray-500 mb-2">Próximas caducidades</div>
          <ul className="text-sm space-y-1 max-h-28 overflow-auto">
            {resumen?.proximas_caducidades?.length ? (
              resumen.proximas_caducidades.map((d) => {
                const soon = isSoon(d.fecha_caducidad, 30);
                return (
                  <li key={d.id} className="flex items-center justify-between">
                    <span className="truncate">
                      <span className={`inline-block w-2 h-2 mr-2 rounded-full ${soon ? "bg-red-500" : "bg-amber-400"}`} />
                      {safe(d.tipo || d.categoria)} · {safe(d.nombre_archivo)}
                    </span>
                    <span className="font-mono text-gray-700">{fmtDate(d.fecha_caducidad)}</span>
                  </li>
                );
              })
            ) : (
              <li className="text-gray-400">—</li>
            )}
          </ul>
        </div>
      </section>

      {/* Tabs */}
      <nav className="flex gap-2">
        {["documentos", "conductores", "datos", "actividad", "facturacion"].map((key) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={`px-3 py-2 rounded-2xl ${tab === key ? "bg-gray-900 text-white" : "bg-white text-gray-700 hover:bg-gray-100"} shadow`}
          >
            {key === "documentos" && "Documentos"}
            {key === "conductores" && "Conductores"}
            {key === "datos" && "Datos personales"}
            {key === "actividad" && "Actividad"}
            {key === "facturacion" && "Facturación"}
          </button>
        ))}
      </nav>

      {/* Contenido de pestañas */}
      <section className="bg-white rounded-2xl shadow p-5">
        {tab === "documentos" && (
          SAFE_DOCS ? (
            <div className="text-sm text-gray-500">Documentos desactivados en modo seguro.</div>
          ) : (
            <TabDocumentos
              docs={docs}
              socio={socio}
              puedeAdministrar={puedeAdministrar}
              onOpen={(cat) => openDocs(cat || null)}
            />
          )
        )}

        {tab === "conductores" && (
          <ConductoresTab
            socio={socio}
            docs={docs}
            puedeAdministrar={puedeAdministrar}
            conductoresUI={conductoresUI}
            loadConductores={loadConductores}
            crearConductor={crearConductor}
            newCond={newCond}
            setNewCond={setNewCond}
            creatingCond={creatingCond}
            openDocs={openDocs}
            eliminarConductor={eliminarConductor}
            darDeBajaConductor={darDeBajaConductor}
          />
        )}

        {tab === "datos" && (
          <TabDatos
            socio={socio}
            fullName={fullName}
            form={form}
            setForm={setForm}
            editing={editing}
            setEditing={setEditing}
            saving={saving}
            onSave={saveDatos}
            puedeAdministrar={puedeAdministrar}
          />
        )}

        {tab === "actividad" && <TabActividad />}

        {tab === "facturacion" && <TabFacturacion />}
      </section>

      {/* Modal gestor */}
      {!SAFE_DOCS && (
        <ModalDocumentos
          isOpen={modalOpen}
          onClose={() => {
            setModalOpen(false);
            reloadDocsAndResumen();
            setChecklistRefreshKey((v) => v + 1);
          }}
          socio={{ id: socioId, nombre: socio?.nombre, matricula: socio?.matricula }}
          puedeEliminar={puedeAdministrar}
          defaultCategoria={initialCategory}
          defaultSubcarpeta={docsSub}
          onChanged={() => {
            reloadDocsAndResumen();
            setChecklistRefreshKey((v) => v + 1);
          }}
        />
      )}

      {/* Modal “Dar de baja” */}
      {bajaModalOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => setBajaModalOpen(false)} />
          <div className="relative w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
            <h3 className="text-lg font-semibold">Dar de baja al socio</h3>
            <p className="mt-2 text-sm text-gray-600">
              Selecciona la fecha efectiva de baja. Este dato aparecerá en la ficha del socio.
            </p>
            <div className="mt-4">
              <label className="block text-sm text-gray-700 mb-1">Fecha de baja</label>
              <input
                type="date"
                value={bajaDate}
                onChange={(e) => setBajaDate(e.target.value)}
                className="w-full rounded-xl border px-3 py-2"
                max={new Date().toISOString().slice(0,10)}
              />
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button
                className="rounded-xl border px-4 py-2 text-sm hover:bg-gray-50"
                onClick={() => setBajaModalOpen(false)}
                disabled={savingBaja}
              >
                Cancelar
              </button>
              <button
                className="rounded-xl bg-red-600 px-4 py-2 text-sm text-white hover:bg-red-700 disabled:opacity-60"
                onClick={() => marcarBaja(bajaDate)}
                disabled={savingBaja || !bajaDate}
              >
                {savingBaja ? "Guardando…" : "Confirmar baja"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* -------------------- Subcomponentes -------------------- */
function CategoriaChip({ label, category, onOpen, disabled = false }) {
  return (
    <button
      onClick={() => !disabled && onOpen(category)}
      disabled={disabled}
      className={`px-3 py-1 rounded-full text-sm border shadow-sm
        ${disabled ? "bg-gray-100 text-gray-400 cursor-not-allowed" : "bg-white hover:bg-gray-50"}`}
      title={disabled ? "Sin permisos" : `Abrir documentos de ${label}`}
    >
      {label}
    </button>
  );
}

function TabDocumentos({ docs, socio, onOpen, puedeAdministrar }) {
  const [openCats, setOpenCats] = useState({});
  const [openSubs, setOpenSubs] = useState({});

  // === Filtros de lista ===
  const [query, setQuery] = useState("");
  const [vencimiento, setVencimiento] = useState("all"); // all | soon | expired

  function fileStatus(fecha) {
    if (!fecha) return "ok";
    const hoy = new Date(); hoy.setHours(0, 0, 0, 0);
    const f = new Date(`${fecha}T00:00:00`);
    const diff = Math.ceil((f - hoy) / 86400000);
    if (diff < 0) return "expired";
    if (diff <= 30) return "soon";
    return "ok";
  }

  function matchesFilters(f) {
    const q = query.trim().toLowerCase();
    const nameOk = !q || (f.nombre || "").toLowerCase().includes(q);
    const st = fileStatus(f.fecha_caducidad);
    const vencOk =
      vencimiento === "all" ||
      (vencimiento === "soon" && st === "soon") ||
      (vencimiento === "expired" && st === "expired");
    return nameOk && vencOk;
  }

  const normalize = (s) =>
    (s || "").toString().trim().toUpperCase().replace(/\s+/g, "_");

  const basePrefix = useMemo(() => {
    const nom = normalize(socio?.nombre || "NOMBRE");
    const mat = normalize(socio?.matricula || "0000XXX");
    return `/uploads/${nom}_${mat}/`;
  }, [socio]);

  // grupos[CAT][SUB] = [{ id, nombre, fecha_caducidad, path }]
  const grupos = useMemo(() => {
    const g = {};
    for (const d of Array.isArray(docs) ? docs : []) {
      const cat = normalize(d.categoria || d.tipo) || "OTROS";
      const path = d.archivo || null;
      let sub = "";

      if (path && path.startsWith(basePrefix)) {
        const resto = path.slice(basePrefix.length);
        const partes = resto.split("/").filter(Boolean);
        if (partes.length >= 2) {
          const catFromPath = normalize(partes[0]);
          const subs = partes.slice(1, -1);
          if (catFromPath === cat && subs.length) sub = subs.join("/");
        }
      }

      if (!g[cat]) g[cat] = {};
      if (!g[cat][sub]) g[cat][sub] = [];
      const docId =
      d?.id ??
      d?.documento_id ??
      d?.doc_id ??
      d?.file_id ??
      d?.archivo_id ??
      null;
      g[cat][sub].push({
        id: docId,
        nombre: d.nombre_archivo || d.nombre || "documento",
        fecha_caducidad: d.fecha_caducidad || null,
        path,
      });
    }

    // orden por caducidad (próximos primero)
    for (const cat of Object.keys(g)) {
      for (const sub of Object.keys(g[cat])) {
        g[cat][sub].sort((a, b) => {
          const da = a.fecha_caducidad ? new Date(a.fecha_caducidad) : null;
          const db = b.fecha_caducidad ? new Date(b.fecha_caducidad) : null;
          if (!da && !db) return 0;
          if (!da) return 1;
          if (!db) return -1;
          return da - db;
        });
      }
    }
    return g;
  }, [docs, basePrefix]);

  const totalDocs = useMemo(
    () =>
      Object.values(grupos).reduce(
        (acc, submap) => acc + Object.values(submap).reduce((a, arr) => a + arr.length, 0),
        0
      ),
    [grupos]
  );

  const collapseByDefault = totalDocs > 30;
  const isCatOpen = (cat) => (cat in openCats ? openCats[cat] : !collapseByDefault);
  const isSubOpen = (cat, sub) => {
    const key = `${cat}::${sub}`;
    return key in openSubs ? openSubs[key] : !collapseByDefault;
  };

  const toggleCat = (cat) => setOpenCats((m) => ({ ...m, [cat]: !isCatOpen(cat) }));
  const toggleSub = (cat, sub) => {
    const key = `${cat}::${sub}`;
    setOpenSubs((m) => ({ ...m, [key]: !isSubOpen(cat, sub) }));
  };

  const expandAll = () => {
    const newCats = {};
    const newSubs = {};
    for (const cat of Object.keys(grupos)) {
      newCats[cat] = true;
      for (const sub of Object.keys(grupos[cat])) newSubs[`${cat}::${sub}`] = true;
    }
    setOpenCats(newCats);
    setOpenSubs(newSubs);
  };

  const collapseAll = () => {
    const newCats = {};
    const newSubs = {};
    for (const cat of Object.keys(grupos)) {
      newCats[cat] = false;
      for (const sub of Object.keys(grupos[cat])) newSubs[`${cat}::${sub}`] = false;
    }
    setOpenCats(newCats);
    setOpenSubs(newSubs);
  };

  const order = ["DATOS_PERSONALES", "DOCUMENTOS_VEHÍCULO", "LIQUIDACIONES", "OTROS"];
  const categorias = useMemo(() => {
    const keys = Object.keys(grupos);
    const known = order.filter((k) => keys.includes(k));
    const extras = keys.filter((k) => !order.includes(k)).sort();
    return [...known, ...extras];
  }, [grupos]);

  const human = (k) => k.replace(/_/g, " ");

  const DocsList = ({ files }) => {
  const visibles = files.filter(matchesFilters);
  if (!visibles.length) {
    return <div className="py-2 text-sm text-gray-500">No hay documentos que coincidan.</div>;
  }

  const openBy = (e, f) => {
    // super defensivo: corta TODO
    if (e) {
      e.preventDefault?.();
      e.stopPropagation?.();
      // corta handlers de fase captura ajenos
      e.nativeEvent?.stopImmediatePropagation?.();
    }
    console.debug("[DocsList] intento abrir:", { id: f.id, path: f.path, nombre: f.nombre });
    if (f?.id) {
      openDocSecure(f.id, f.nombre);
    } else if (f?.path) {
      const url = `/api/documentos/open-by-path?path=${encodeURIComponent(f.path)}`;
      window.open(url, "_blank", "noreferrer");
    } else {
      alert("No se puede abrir este documento: falta identificador o ruta.");
    }
  };

  const onKey = (e, f) => {
    if (e.key === "Enter" || e.key === " ") openBy(e, f);
  };

  return (
    <div className="divide-y">
      {visibles.map((f, i) => (
        <div key={f.id ?? f.path ?? `${f.nombre}-${i}`} className="flex items-center justify-between py-2">
          <div className="min-w-0">
            {/* NOMBRE: usa botón y varios eventos por si algún padre captura el click */}
            <button
              type="button"
              className="font-medium truncate text-left text-blue-700 hover:underline cursor-pointer"
              title="Abrir en pestaña nueva o descargar"
              onClick={(e) => openBy(e, f)}
              onPointerDown={(e) => openBy(e, f)}
              onMouseDown={(e) => openBy(e, f)}
              onKeyDown={(e) => onKey(e, f)}
              tabIndex={0}
              aria-label={`Abrir ${f.nombre}`}
              style={{ pointerEvents: "auto" }}
            >
              {f.nombre}
            </button>

            <div className="text-xs text-gray-500">
              {f.fecha_caducidad ? `caduca: ${fmtDate(f.fecha_caducidad)}` : "sin caducidad"}
            </div>
          </div>

          <div className="flex items-center gap-2">
            <BadgeEstado fecha={f.fecha_caducidad} />
            <button
              type="button"
              onClick={(e) => openBy(e, f)}
              onPointerDown={(e) => openBy(e, f)}
              className="text-xs px-2 py-1 rounded-2xl border hover:bg-gray-100"
              title="Abrir documento"
              style={{ pointerEvents: "auto" }}
            >
              Abrir
            </button>
          </div>
        </div>
      ))}
    </div>
  );
};

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <div className="text-lg font-semibold">Documentos por carpeta</div>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Buscar por nombre…"
              className="border rounded-xl px-3 py-2 w-56"
            />
            <select
              value={vencimiento}
              onChange={(e) => setVencimiento(e.target.value)}
              className="border rounded-xl px-3 py-2"
              title="Filtrar por caducidad"
            >
              <option value="all">Todos</option>
              <option value="soon">Caduca ≤ 30 días</option>
              <option value="expired">Caducados</option>
            </select>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={expandAll}
            className="px-3 py-1.5 rounded-2xl border hover:bg-gray-100 text-sm"
            title="Expandir todo"
          >
            Expandir todo
          </button>
          <button
            onClick={collapseAll}
            className="px-3 py-1.5 rounded-2xl border hover:bg-gray-100 text-sm"
            title="Contraer todo"
          >
            Contraer todo
          </button>
          {puedeAdministrar && (
            <button
              onClick={() => onOpen?.(null)}
              className="px-3 py-2 rounded-2xl bg-blue-600 text-white shadow hover:bg-blue-700"
            >
              Abrir gestor
            </button>
          )}
        </div>
      </div>

      {!Object.keys(grupos).length ? (
        <div className="text-gray-500">No hay documentos aún.</div>
      ) : (
        (() => {
          const keys = Object.keys(grupos);
          const order = ["DATOS_PERSONALES", "DOCUMENTOS_VEHÍCULO", "LIQUIDACIONES", "OTROS"];
          const known = order.filter((k) => keys.includes(k));
          const extras = keys.filter((k) => !order.includes(k)).sort();
          const categorias = [...known, ...extras];

          return categorias.map((cat) => {
            const catCount = Object.values(grupos[cat]).reduce((acc, arr) => acc + arr.length, 0);
            const allSubs = Object.keys(grupos[cat]).sort((a, b) => a.localeCompare(b));
            const namedSubs = allSubs.filter((s) => s !== "");
            const rootDocs = grupos[cat][""] || [];

            return (
              <div key={cat} className="border rounded-2xl overflow-hidden bg-white shadow-sm">
                <div className="flex items-center justify-between px-4 py-3 bg-gray-50">
                  <button
                    onClick={() => toggleCat(cat)}
                    className="flex items-center gap-2 font-semibold"
                    title={isCatOpen(cat) ? "Contraer" : "Expandir"}
                    aria-expanded={isCatOpen(cat)}
                  >
                    <span className="text-lg">{isCatOpen(cat) ? "▾" : "▸"}</span>
                    {human(cat)}
                    <span className="ml-2 text-xs text-gray-500">({catCount})</span>
                  </button>
                  {puedeAdministrar && (
                    <button
                      onClick={() => onOpen?.(cat)}
                      className="text-sm px-3 py-1.5 rounded-2xl border hover:bg-gray-100"
                      title={`Abrir gestor en ${human(cat)}`}
                    >
                      Abrir gestor
                    </button>
                  )}
                </div>

                {isCatOpen(cat) && (
                  <>
                    {rootDocs.length > 0 && (
                      <div className="px-4 py-3 border-t">
                        <DocsList files={rootDocs} />
                      </div>
                    )}

                    {namedSubs.map((sub) => {
                      const count = grupos[cat][sub].length;
                      return (
                        <div key={sub} className="px-4 py-3 border-t">
                          <button
                            onClick={() => toggleSub(cat, sub)}
                            className="mb-2 text-sm text-gray-700 flex items-center gap-2"
                            title={isSubOpen(cat, sub) ? "Contraer" : "Expandir"}
                            aria-expanded={isSubOpen(cat, sub)}
                            aria-controls={`${cat}-${sub}`.replace(/\s/g, "_")}
                          >
                            <span className="text-base">{isSubOpen(cat, sub) ? "▾" : "▸"}</span>
                            {`📁 ${sub}`}
                            <span className="ml-2 text-xs text-gray-500">({count})</span>
                          </button>

                          {isSubOpen(cat, sub) && <DocsList files={grupos[cat][sub]} />}
                        </div>
                      );
                    })}
                  </>
                )}
              </div>
            );
          });
        })()
      )}
    </div>
  );
}

/* -------- Conductores Tab (cards + vista visual para socios) -------- */
function ConductoresTab({
  socio,
  docs,
  puedeAdministrar,
  conductoresUI,
  loadConductores,
  crearConductor,
  newCond,
  setNewCond,
  creatingCond,
  openDocs,
  eliminarConductor,
  darDeBajaConductor,
}) {
  // <<< AÑADIR DESDE AQUÍ >>>
  // 1) Estadísticas reales a partir de los documentos
  const stats = useMemo(() => deriveConductoresStats(docs, socio), [docs, socio]);

  // 2) Mapa nombre -> { dpCount, coopCount, nominasCount }
  const mapStats = useMemo(() => new Map(stats.map((s) => [s.nombre, s])), [stats]);

  // 3) Normalizador de nombre “seguro”
  const toSafeName = (v) => (typeof v === "string" && v.trim()) ? v.trim() : "SIN NOMBRE";

  // 4) Lista base (carpetas de 1er nivel traídas del backend, si existen)
  const listBase = (Array.isArray(conductoresUI.list) ? conductoresUI.list : [])
    .filter((n) => typeof n === "string" && n.trim().length > 0);

  // 5) Lista final de conductores con contadores
  const all = (listBase.length ? listBase : stats.map((s) => s.nombre))
    .map((nombreRaw) => {
      const nombre = toSafeName(nombreRaw);
      return {
        nombre,
        dpCount: mapStats.get(nombre)?.dpCount || 0,
        coopCount: mapStats.get(nombre)?.coopCount || 0,
        nominasCount: mapStats.get(nombre)?.nominasCount || 0,
      };
    })
    .sort((a, b) => a.nombre.localeCompare(b.nombre, "es"));

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-lg font-semibold">Conductores asociados</div>
          <div className="text-sm text-gray-500">
            {puedeAdministrar
              ? "Fichas visuales de cada conductor. Puedes abrir el gestor o administrar."
              : "Consulta aquí los datos y las nóminas de cada conductor."}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={loadConductores}
            className="text-sm px-3 py-1.5 rounded-2xl border hover:bg-gray-100"
            disabled={conductoresUI.loading}
          >
            {conductoresUI.loading ? "Actualizando…" : "Actualizar"}
          </button>
          {puedeAdministrar && (
            <button
              onClick={() => openDocs("CONDUCTORES")}
              className="px-3 py-2 rounded-2xl bg-blue-600 text-white shadow hover:bg-blue-700"
            >
              Abrir gestor
            </button>
          )}
        </div>
      </div>

      {puedeAdministrar && (
        <div className="border rounded-2xl p-4 bg-gray-50">
          <div className="font-medium mb-2">Alta de conductor</div>
          <div className="grid sm:grid-cols-3 gap-3">
            <input
              className="border rounded-xl px-3 py-2"
              placeholder="Nombre"
              value={newCond.nombre}
              onChange={(e) => setNewCond((c) => ({ ...c, nombre: e.target.value }))}
            />
            <input
              className="border rounded-xl px-3 py-2"
              placeholder="Apellidos"
              value={newCond.apellidos}
              onChange={(e) => setNewCond((c) => ({ ...c, apellidos: e.target.value }))}
            />
            <input
              className="border rounded-xl px-3 py-2"
              placeholder="DNI (opcional)"
              value={newCond.dni}
              onChange={(e) => setNewCond((c) => ({ ...c, dni: e.target.value }))}
            />
          </div>
          <div className="mt-3 flex justify-end">
            <button
              onClick={crearConductor}
              disabled={creatingCond}
              className="px-4 py-2 rounded-2xl bg-green-600 text-white hover:bg-green-700 disabled:opacity-60"
            >
              {creatingCond ? "Creando…" : "Crear conductor"}
            </button>
          </div>
          <p className="mt-2 text-xs text-gray-500">
            Se crearán automáticamente: <code>CONDUCTORES/&lt;Nombre Apellidos&gt;/</code>,{" "}
            <code>.../DATOS_PERSONALES/</code> y <code>.../NOMINAS/</code>.
          </p>
        </div>
      )}

      {conductoresUI.err ? (
        <div className="p-4 text-sm text-red-600 border rounded-2xl bg-red-50">{conductoresUI.err}</div>
      ) : conductoresUI.loading && !all.length ? (
        <div className="p-4 text-sm text-gray-500">Cargando…</div>
      ) : !all.length ? (
        <div className="p-4 text-sm text-gray-500 border rounded-2xl">No hay conductores dados de alta aún.</div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {all.map(({ nombre, dpCount, coopCount, nominasCount }) => (
            <ConductorCard
              key={nombre}
              name={nombre}
              socio={socio}
              docs={docs}
              dpCount={dpCount}
              coopCount={coopCount}
              nominasCount={nominasCount}
              puedeAdministrar={puedeAdministrar}
              onEliminar={() => eliminarConductor(nombre)}
              onBaja={() => darDeBajaConductor(nombre)}
              onOpenGestorDP={() => openDocs("CONDUCTORES", `${nombre}/DATOS_PERSONALES`)}
              onOpenGestorNom={() => openDocs("CONDUCTORES", `${nombre}/NOMINAS`)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function ConductorCard({
  name,
  socio,
  docs,
  dpCount = 0,
  coopCount = 0,
  nominasCount = 0,
  puedeAdministrar,
  onEliminar,
  onBaja,
  onOpenGestorDP,
  onOpenGestorNom,
}) {
  const [showFicha, setShowFicha] = useState(false);
  const [showDP, setShowDP] = useState(false);
  const [showCoop, setShowCoop] = useState(false);
  const [showNom, setShowNom] = useState(false);

  // Accesibilidad: refs para foco
  const fichaBtnRef = useRef(null);
  const dpBtnRef = useRef(null);
  const nomBtnRef = useRef(null);

  // Datos del conductor (ficha)
  const [datos, setDatos] = useState({
    telefono: "",
    dni: "",
    email: "",
    naf: "",
    cuenta_bancaria: "",
    direccion: "",
    neto: "",
  });
  const [loadingFicha, setLoadingFicha] = useState(false);
  const [savingFicha, setSavingFicha] = useState(false);
  const [editingFicha, setEditingFicha] = useState(false); // sólo empleados

  // Carga perezosa al abrir la ficha
  useEffect(() => {
    if (!showFicha) return;
    let alive = true;
    (async () => {
      setLoadingFicha(true);
      try {
        const d = await fetchConductorDatos(socio?.id, name);
        if (alive) setDatos(d);
      } catch (e) {
        if (alive) {
          setDatos((prev) => prev);
          console.warn("Ficha conductor:", e?.message || e);
        }
      } finally {
        if (alive) setLoadingFicha(false);
      }
    })();
    return () => { alive = false; };
  }, [showFicha, socio?.id, name]);

  // Listado inline de ficheros
  const files = useMemo(() => listFilesForConductor(docs, socio, name), [docs, socio, name]);

  // Paginación simple
  const PAGE_SIZE = 20;
  const [dpPage, setDpPage] = useState(1);
  const [coopPage, setCoopPage] = useState(1);
  const [nomPage, setNomPage] = useState(1);
  useEffect(() => { setDpPage(1); }, [showDP, name]);
  useEffect(() => { setCoopPage(1); }, [showCoop, name]);
  useEffect(() => { setNomPage(1); }, [showNom, name]);

  const dpTotal = files.dp.length;
  const coopTotal = files.coop.length;
  const nomTotal = files.nominas.length;
  const dpPages = Math.max(1, Math.ceil(dpTotal / PAGE_SIZE));
  const coopPages = Math.max(1, Math.ceil(coopTotal / PAGE_SIZE));
  const nomPages = Math.max(1, Math.ceil(nomTotal / PAGE_SIZE));
  const dpSlice = files.dp.slice((dpPage - 1) * PAGE_SIZE, dpPage * PAGE_SIZE);
  const coopSlice = files.coop.slice((coopPage - 1) * PAGE_SIZE, coopPage * PAGE_SIZE);
  const nomSlice = files.nominas.slice((nomPage - 1) * PAGE_SIZE, nomPage * PAGE_SIZE);

  const safeKey = (typeof name === "string" ? name : "SIN_NOMBRE").replace(/\s+/g, "_");
  const dpId = `dp-${safeKey}`;
  const nomId = `nom-${safeKey}`;
  const fichaId = `ficha-${safeKey}`;

  // Validaciones ligeras
  const emailOk = !datos.email || /^\S+@\S+\.\S+$/.test(datos.email);
  const netoOk = !datos.neto || !isNaN(Number(datos.neto));
  const canSave = puedeAdministrar && editingFicha && emailOk && netoOk && !savingFicha;

  return (
    <div className="rounded-2xl border bg-white shadow-sm p-4">
      <div className="flex items-center gap-4">
        <img
          src={avatarFromName(name)}
          alt={name}
          className="w-16 h-16 rounded-full border"
          width={64}
          height={64}
          loading="lazy"
          decoding="async"
        />
        <div className="min-w-0 flex-1">
          <div className="font-semibold text-lg truncate">{name}</div>
          <div className="mt-1 flex flex-wrap items-center gap-2 text-xs">
            <span className="px-2 py-0.5 rounded-full bg-blue-100 text-blue-800">Datos personales: {dpCount}</span>
            <span className="px-2 py-0.5 rounded-full bg-sky-100 text-sky-800">Datos cooperativa: {coopCount}</span>
            <span className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800">Nóminas: {nominasCount}</span>
          </div>
        </div>

        {/* Acciones de administración */}
        {puedeAdministrar && (
          <div className="flex items-center gap-2">
            <div className="relative">
              <details className="group">
                <summary className="list-none px-3 py-1.5 rounded-2xl border hover:bg-gray-50 text-sm cursor-pointer" aria-label="Acciones de administración">
                  ⋮
                </summary>
                <div className="absolute right-0 mt-2 w-56 rounded-xl border bg-white shadow-lg p-1 z-10">
                  <button onClick={() => onBaja?.()} className="w-full text-left px-3 py-2 rounded-lg hover:bg-gray-50 text-sm">
                    Marcar como de baja
                  </button>
                  <button onClick={() => onEliminar?.()} className="w-full text-left px-3 py-2 rounded-lg hover:bg-red-50 text-sm text-red-600">
                    Eliminar conductor
                  </button>
                  <div className="my-1 border-t" />
                  <button onClick={() => onOpenGestorDP?.()} className="w-full text-left px-3 py-2 rounded-lg hover:bg-gray-50 text-sm">
                    Abrir gestor (Datos personales)
                  </button>
                  <button onClick={() => onOpenGestorNom?.()} className="w-full text-left px-3 py-2 rounded-lg hover:bg-gray-50 text-sm">
                    Abrir gestor (Nóminas)
                  </button>
                </div>
              </details>
            </div>
          </div>
        )}
      </div>

      {/* === Acordeón: Ficha del conductor === */}
      <div className="mt-4">
        <button
          ref={fichaBtnRef}
          onClick={() => { setShowFicha((v) => !v); setTimeout(() => fichaBtnRef.current?.focus(), 0); }}
          className="px-3 py-1.5 rounded-2xl border hover:bg-gray-50 text-sm"
          aria-expanded={showFicha}
          aria-controls={fichaId}
        >
          {showFicha ? "Ocultar ficha" : "Ver ficha del conductor"}
        </button>

        {showFicha && (
          <div id={fichaId} className="mt-3">
            {loadingFicha ? (
              <div className="text-sm text-gray-500">Cargando ficha…</div>
            ) : (
              <div className="space-y-3">
                <div className="grid md:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Teléfono</label>
                    <input
                      className="w-full border rounded-xl px-3 py-2"
                      value={datos.telefono}
                      disabled={!puedeAdministrar || !editingFicha}
                      onChange={(e) => setDatos((d) => ({ ...d, telefono: e.target.value }))}
                      placeholder="600 000 000"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">DNI/NIE</label>
                    <input
                      className="w-full border rounded-xl px-3 py-2"
                      value={datos.dni}
                      disabled={!puedeAdministrar || !editingFicha}
                      onChange={(e) => setDatos((d) => ({ ...d, dni: e.target.value }))}
                      placeholder="00000000X"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Correo</label>
                    <input
                      type="email"
                      className={`w-full border rounded-xl px-3 py-2 ${(!datos.email || /^\S+@\S+\.\S+$/.test(datos.email)) ? "" : "border-red-400"}`}
                      value={datos.email}
                      disabled={!puedeAdministrar || !editingFicha}
                      onChange={(e) => setDatos((d) => ({ ...d, email: e.target.value }))}
                      placeholder="correo@dominio.com"
                    />
                    {datos.email && !/^\S+@\S+\.\S+$/.test(datos.email) && <div className="text-xs text-red-600 mt-1">Correo no válido</div>}
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">NAF (Seguridad Social)</label>
                    <input
                      className="w-full border rounded-xl px-3 py-2"
                      value={datos.naf}
                      disabled={!puedeAdministrar || !editingFicha}
                      onChange={(e) => setDatos((d) => ({ ...d, naf: e.target.value }))}
                      placeholder="12 dígitos…"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Cuenta bancaria (IBAN)</label>
                    <input
                      className="w-full border rounded-xl px-3 py-2"
                      value={datos.cuenta_bancaria}
                      disabled={!puedeAdministrar || !editingFicha}
                      onChange={(e) => setDatos((d) => ({ ...d, cuenta_bancaria: e.target.value }))}
                      placeholder="ES00 0000 0000 0000 0000 0000"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Importe neto nómina (€)</label>
                    <input
                      type="number"
                      step="0.01"
                      className={`w-full border rounded-xl px-3 py-2 ${(!datos.neto || !isNaN(Number(datos.neto))) ? "" : "border-red-400"}`}
                      value={datos.neto}
                      disabled={!puedeAdministrar || !editingFicha}
                      onChange={(e) => setDatos((d) => ({ ...d, neto: e.target.value }))}
                      placeholder="0.00"
                    />
                    {datos.neto && isNaN(Number(datos.neto)) && <div className="text-xs text-red-600 mt-1">Introduce un número válido</div>}
                  </div>
                </div>

                <div>
                  <label className="block text-xs text-gray-600 mb-1">Dirección</label>
                  <textarea
                    className="w-full border rounded-xl px-3 py-2"
                    value={datos.direccion}
                    disabled={!puedeAdministrar || !editingFicha}
                    onChange={(e) => setDatos((d) => ({ ...d, direccion: e.target.value }))}
                    placeholder="Calle, número, piso, CP, municipio…"
                    rows={2}
                  />
                </div>

                {/* Controles de edición */}
                {puedeAdministrar ? (
                  !editingFicha ? (
                    <div className="flex justify-end">
                      <button
                        onClick={() => setEditingFicha(true)}
                        className="px-3 py-2 rounded-2xl bg-white border shadow hover:bg-gray-50 text-sm"
                      >
                        Editar ficha
                      </button>
                    </div>
                  ) : (
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={() => {
                          setEditingFicha(false);
                          setLoadingFicha(true);
                          fetchConductorDatos(socio?.id, name)
                            .then((d) => setDatos(d))
                            .catch(() => {})
                            .finally(() => setLoadingFicha(false));
                        }}
                        className="px-3 py-2 rounded-2xl bg-white border shadow hover:bg-gray-50 text-sm"
                        disabled={savingFicha}
                      >
                        Cancelar
                      </button>
                      <button
                        onClick={async () => {
                          if (!puedeAdministrar || savingFicha) return;
                          setSavingFicha(true);
                          try {
                            await saveConductorDatos(socio?.id, name, datos);
                            setEditingFicha(false);
                          } catch (e) {
                            alert(e?.message || "No se pudo guardar");
                          } finally {
                            setSavingFicha(false);
                          }
                        }}
                        className="px-3 py-2 rounded-2xl bg-green-600 text-white shadow hover:bg-green-700 text-sm disabled:opacity-60"
                        disabled={savingFicha}
                      >
                        {savingFicha ? "Guardando…" : "Guardar cambios"}
                      </button>
                    </div>
                  )
                ) : null}
              </div>
            )}
          </div>
        )}
      </div>

      {/* === Desplegable: Datos personales (archivos) === */}
      <div className="mt-4">
        <button
          ref={dpBtnRef}
          onClick={() => { setShowDP((v) => !v); setTimeout(() => dpBtnRef.current?.focus(), 0); }}
          className="px-3 py-1.5 rounded-2xl border hover:bg-gray-50 text-sm"
          aria-expanded={showDP}
          aria-controls={dpId}
        >
          {showDP ? "Ocultar datos personales" : "Ver datos personales"}
        </button>

        {showDP && (
          <div id={dpId} className="mt-3 space-y-2">
            <div className="flex items-center justify-between">
              <div className="text-sm font-medium">Archivos de datos personales</div>
              {files.dp.length > 0 && (
                <button
                  onClick={() => downloadZipForPaths(
                    socio?.id,
                    [`CONDUCTORES/${name}/DATOS_PERSONALES`],
                    `DP_${safeKey}.zip`
                  )}
                  className="text-xs px-2 py-1 rounded-2xl border hover:bg-gray-100"
                >
                  Descargar todo (zip)
                </button>
              )}
            </div>

            {files.dp.slice((dpPage - 1) * 20, dpPage * 20).length ? (
              <ul className="space-y-1">
                {files.dp.slice((dpPage - 1) * 20, dpPage * 20).map((f) => (
                  <li key={f.id} className="flex items-center justify-between">
                    <button
                      type="button"
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); openDocSecure(f.id, f.nombre); }}
                      className="text-blue-700 hover:underline truncate text-left"
                      title="Abrir documento"
                    >
                      {f.nombre}
                    </button>
                    <div className="flex items-center gap-2">
                      <BadgeEstado fecha={f.fecha_caducidad} />
                      <button
                        type="button"
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); openDocSecure(f.id, f.nombre); }}
                        className="text-xs px-2 py-1 rounded-2xl border hover:bg-gray-100"
                      >
                        Abrir
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="text-sm text-gray-500">No hay documentos en esta carpeta.</div>
            )}

            {files.dp.length > 20 && (
              <div className="flex items-center justify-end gap-2 pt-2">
                <span className="text-xs text-gray-500">Página {dpPage} de {Math.max(1, Math.ceil(files.dp.length / 20))}</span>
                <button
                  className="text-xs px-2 py-1 rounded border hover:bg-gray-50 disabled:opacity-50"
                  disabled={dpPage <= 1}
                  onClick={() => setDpPage((p) => Math.max(1, p - 1))}
                >
                  ←
                </button>
                <button
                  className="text-xs px-2 py-1 rounded border hover:bg-gray-50 disabled:opacity-50"
                  disabled={dpPage >= Math.ceil(files.dp.length / 20)}
                  onClick={() => setDpPage((p) => Math.min(Math.ceil(files.dp.length / 20), p + 1))}
                >
                  →
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* === Desplegable: Datos cooperativa (archivos) === */}
<div className="mt-4">
  <button
    onClick={() => setShowCoop((v) => !v)}
    className="px-3 py-1.5 rounded-2xl border hover:bg-gray-50 text-sm"
    aria-expanded={showCoop}
    aria-controls={`coop-${name.replace(/\s+/g, "_")}`}
  >
    {showCoop ? "Ocultar datos cooperativa" : "Ver datos cooperativa"}
  </button>

  {showCoop && (
    <div id={`coop-${name.replace(/\s+/g, "_")}`} className="mt-3 space-y-2">
      <div className="flex items-center justify-between">
        <div className="text-sm font-medium">Archivos de datos cooperativa</div>
        {files.coop.length > 0 && (
          <button
            onClick={() =>
              downloadZipForPaths(
                socio?.id,
                [`CONDUCTORES/${name}/DATOS_COOPERATIVA`],
                `COOPERATIVA_${safeKey}.zip`
              )
            }
            className="text-xs px-2 py-1 rounded-2xl border hover:bg-gray-100"
          >
            Descargar todo (zip)
          </button>
        )}
      </div>

      {coopSlice.length ? (
        <ul className="space-y-1">
          {coopSlice.map((f) => (
            <li key={f.id} className="flex items-center justify-between">
              <button
                type="button"
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); openDocSecure(f.id, f.nombre); }}
                className="text-blue-700 hover:underline truncate text-left"
                title="Abrir documento"
              >
                {f.nombre}
              </button>
              <div className="flex items-center gap-2">
                <BadgeEstado fecha={f.fecha_caducidad} />
                <button
                  type="button"
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); openDocSecure(f.id, f.nombre); }}
                  className="text-xs px-2 py-1 rounded-2xl border hover:bg-gray-100"
                >
                  Abrir
                </button>
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <div className="text-sm text-gray-500">No hay documentos en esta carpeta.</div>
      )}

      {files.coop.length > PAGE_SIZE && (
        <div className="flex items-center justify-end gap-2 pt-2">
          <span className="text-xs text-gray-500">
            Página {coopPage} de {coopPages}
          </span>
          <button
            className="text-xs px-2 py-1 rounded border hover:bg-gray-50 disabled:opacity-50"
            disabled={coopPage <= 1}
            onClick={() => setCoopPage((p) => Math.max(1, p - 1))}
          >
            ←
          </button>
          <button
            className="text-xs px-2 py-1 rounded border hover:bg-gray-50 disabled:opacity-50"
            disabled={coopPage >= coopPages}
            onClick={() => setCoopPage((p) => Math.min(coopPages, p + 1))}
          >
            →
          </button>
        </div>
      )}
    </div>
  )}
</div>

      {/* === Desplegable: Nóminas (archivos) === */}
      <div className="mt-4">
        <button
          ref={nomBtnRef}
          onClick={() => { setShowNom((v) => !v); setTimeout(() => nomBtnRef.current?.focus(), 0); }}
          className="px-3 py-1.5 rounded-2xl border hover:bg-gray-50 text-sm"
          aria-expanded={showNom}
          aria-controls={nomId}
        >
          {showNom ? "Ocultar nóminas" : "Ver nóminas"}
        </button>

        {showNom && (
          <div id={nomId} className="mt-3 space-y-2">
            <div className="flex items-center justify-between">
              <div className="text-sm font-medium">Archivos de nóminas</div>
              {files.nominas.length > 0 && (
                <button
                  onClick={() => downloadZipForPaths(
                    socio?.id,
                    [`CONDUCTORES/${name}/NOMINAS`],
                    `NOMINAS_${safeKey}.zip`
                  )}
                  className="text-xs px-2 py-1 rounded-2xl border hover:bg-gray-100"
                >
                  Descargar todo (zip)
                </button>
              )}
            </div>

            {files.nominas.slice((nomPage - 1) * 20, nomPage * 20).length ? (
              <ul className="space-y-1">
                {files.nominas.slice((nomPage - 1) * 20, nomPage * 20).map((f) => (
                  <li key={f.id} className="flex items-center justify-between">
                    <button
                      type="button"
                       onClick={(e) => { e.preventDefault(); e.stopPropagation(); openDocSecure(f.id, f.nombre); }}
                      className="text-blue-700 hover:underline truncate text-left"
                      title="Abrir nómina"
                    >
                      {f.nombre}
                    </button>
                    <div className="flex items-center gap-2">
                      <BadgeEstado fecha={f.fecha_caducidad} />
                      <button
                        type="button"
                         onClick={(e) => { e.preventDefault(); e.stopPropagation(); openDocSecure(f.id, f.nombre); }}
                        className="text-xs px-2 py-1 rounded-2xl border hover:bg-gray-100"
                      >
                        Abrir
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="text-sm text-gray-500">No hay nóminas subidas aún.</div>
            )}

            {files.nominas.length > 20 && (
              <div className="flex items-center justify-end gap-2 pt-2">
                <span className="text-xs text-gray-500">Página {nomPage} de {Math.max(1, Math.ceil(files.nominas.length / 20))}</span>
                <button
                  className="text-xs px-2 py-1 rounded border hover:bg-gray-50 disabled:opacity-50"
                  disabled={nomPage <= 1}
                  onClick={() => setNomPage((p) => Math.max(1, p - 1))}
                >
                  ←
                </button>
                <button
                  className="text-xs px-2 py-1 rounded border hover:bg-gray-50 disabled:opacity-50"
                  disabled={nomPage >= Math.ceil(files.nominas.length / 20)}
                  onClick={() => setNomPage((p) => Math.min(Math.ceil(files.nominas.length / 20), p + 1))}
                >
                  →
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/* -------- Otros tabs -------- */
function TabDatos({ socio, fullName, form, setForm, editing, setEditing, saving, onSave, puedeAdministrar }) {
  const Row = ({ label, value, name, editable = false, type = "text", placeholder }) => (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-2 items-center">
      <div className="text-gray-500">{label}</div>
      <div className="md:col-span-2">
        {editable ? (
          <input
            type={type}
            value={form[name] || ""}
            placeholder={placeholder || ""}
            onChange={(e) => setForm((f) => ({ ...f, [name]: e.target.value }))}
            className="w-full rounded-xl border px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        ) : (
          <div className="font-medium break-words">{safe(value)}</div>
        )}
      </div>
    </div>
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="text-lg font-semibold">Datos personales</div>
        {puedeAdministrar && !editing ? (
          <button onClick={() => setEditing(true)} className="px-3 py-2 rounded-2xl bg-white border shadow hover:bg-gray-50">
            Editar
          </button>
        ) : puedeAdministrar && (
          <div className="flex gap-2">
            <button
              onClick={() => setEditing(false)}
              className="px-3 py-2 rounded-2xl bg-white border shadow hover:bg-gray-50"
              disabled={saving}
            >
              Cancelar
            </button>
            <button
              onClick={onSave}
              className="px-3 py-2 rounded-2xl bg-green-600 text-white shadow hover:bg-green-700 disabled:opacity-60"
              disabled={saving}
            >
              {saving ? "Guardando..." : "Guardar cambios"}
            </button>
          </div>
        )}
      </div>

      <div className="space-y-3">
        <Row label="Nombre" value={fullName} />
        <Row label="DNI" value={socio.dni} />
        <Row label="Matrícula" value={socio.matricula} name="matricula" editable={editing && puedeAdministrar} placeholder="XXXX-XXX" />
        <Row label="Email" value={socio.email} name="email" editable={editing && puedeAdministrar} type="email" placeholder="correo@dominio.com" />
        <Row label="Teléfono" value={socio.telefono} name="telefono" editable={editing && puedeAdministrar} placeholder="600..." />
        <Row label="Dirección" value={socio.direccion} name="direccion" editable={editing && puedeAdministrar} />
        <Row label="CAP" value={socio.cap} />
        <Row label="Permiso conducir" value={socio.permiso_conduccion} />
        <Row label="Tarjeta sanitaria" value={socio.tarjeta_sanitaria} />
        <Row label="Cuenta bancaria" value={socio.cuenta_bancaria} />
      </div>
    </div>
  );
}

function TabActividad() {
  return <div className="text-gray-500">Aquí mostraremos últimas subidas, cambios y avisos (pendiente de integrar).</div>;
}
function TabFacturacion() {
  return (
    <div className="text-gray-500">
      Gráfica y KPIs de facturación (pendiente de integrar). Cuando estés listo, activamos Recharts con datos reales.
    </div>
  );
}

/* -------------------- Más utilidades -------------------- */
function isSoon(dateStr, days = 30) {
  if (!dateStr) return false;
  try {
    const target = new Date(dateStr + "T00:00:00");
    const now = new Date();
    const diff = (target - now) / (1000 * 60 * 60 * 24);
    return diff <= days;
  } catch {
    return false;
  }
}
function daysUntil(dateStr) {
  if (!dateStr) return Infinity;
  const target = new Date(dateStr + "T00:00:00");
  const now = new Date();
  return Math.ceil((target - now) / (1000 * 60 * 60 * 24));
}
function getAlertLevel(resumen) {
  if (!resumen?.proximas_caducidades?.length) return "none";
  const soonest = Math.min(
    ...resumen.proximas_caducidades
      .map((d) => daysUntil(d.fecha_caducidad))
      .filter((n) => Number.isFinite(n))
  );
  if (!Number.isFinite(soonest)) return "none";
  if (soonest <= 7) return "high";
  if (soonest <= 30) return "med";

  return "none";
}
